// Gate a route subtree behind authentication and (optionally) roles.
// Stores the requested URL so post-login redirect can return the user.

import { Suspense, lazy, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  unauthorizedFallback?: ReactNode;
}

function AuthLoading() {
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--fg-muted)',
        background: 'var(--bg)',
      }}
    >
      Loading...
    </div>
  );
}

function DefaultUnauthorized() {
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-7)',
        background: 'var(--bg)',
      }}
    >
      <div className="alert" data-tone="warning" style={{ maxWidth: 480 }}>
        <svg
          className="alert-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div className="alert-body">
          <div className="alert-title">Access denied</div>
          <p style={{ margin: 0 }}>You do not have permission to access this page.</p>
        </div>
      </div>
    </div>
  );
}

export function ProtectedRoute({
  children,
  requiredRoles,
  unauthorizedFallback = <DefaultUnauthorized />,
}: ProtectedRouteProps) {
  const { initialized, authenticated, loading, hasAnyRole } = useAuth();
  const location = useLocation();

  if (loading || !initialized) return <AuthLoading />;

  if (!authenticated) {
    try {
      const returnUrl = `${location.pathname}${location.search}${location.hash}`;
      sessionStorage.setItem('auth:returnUrl', returnUrl);
    } catch {
      // ignore
    }
    return (
      <Suspense fallback={<AuthLoading />}>
        <LoginPage />
      </Suspense>
    );
  }

  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return <>{unauthorizedFallback}</>;
  }

  return <>{children}</>;
}

export function getPostLoginReturnUrl(): string | null {
  try {
    const url = sessionStorage.getItem('auth:returnUrl');
    if (url) sessionStorage.removeItem('auth:returnUrl');
    return url;
  } catch {
    return null;
  }
}
